/**
 * Renders a single time-series chart from the assistant.
 *
 * The assistant's wire format is deliberately small — a title, a chart type, an
 * optional y-axis label and named series of (ISO-8601 timestamp, value) points —
 * so all this component does is map that onto Highcharts, which the console
 * already uses everywhere else. Timestamps become epoch milliseconds for a
 * `datetime` axis; the chart's own background stays transparent so the panel
 * (light or dark) shows through, and text colors are read from the console's
 * CSS variables at render time so the chart follows the active theme.
 *
 * Like every other chart in the app it needs an explicit reflow: the chat panel
 * can be enlarged and shrunk while a chart is on screen, and Highcharts only
 * reacts to window resizes on its own.
 */

import { Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { TimeSeriesChartSpec } from './api/assistant.service';

/** Series palette — the console's standard chart colors. */
const SERIES_COLORS = ['#0273fb', '#08dc5a', '#ffc003', '#ff0d49', '#1aadce', '#910000', '#492970', '#f28f43', '#77a1e5'];

@Component({
    selector: 'app-assistant-chart',
    template: `<div class="assistant-chart" [chart]="chart"></div>`,
    standalone: false,
})
export class AssistantChartComponent implements OnChanges, OnDestroy {
    @Input({ required: true }) spec!: TimeSeriesChartSpec;

    chart?: Chart;

    /** Re-lays-out the plot when its container changes size (e.g. panel expand). */
    private resizeObserver?: ResizeObserver;
    private reflowTimers: any[] = [];
    private lastWidth = 0;

    constructor(private elementRef: ElementRef) {}

    ngOnChanges(): void {
        this.chart = new Chart(this.buildOptions() as any);
        this.scheduleReflows();
        this.observeResize();
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.reflowTimers.forEach((timer) => clearTimeout(timer));
    }

    private buildOptions(): Record<string, any> {
        const text = this.cssVar('--tableText', '#434c5e');
        const subText = this.cssVar('--subText', '#97a0b2');
        const gridLine = this.cssVar('--defaultBorderColor', 'rgba(0, 0, 0, 0.07)');

        return {
            chart: {
                type: this.chartType(),
                height: 220,
                // Let the panel's own background (light or dark) show through.
                backgroundColor: 'transparent',
                style: { fontFamily: "'Open Sans', Arial, Helvetica, sans-serif" },
            },
            colors: SERIES_COLORS,
            credits: { enabled: false },
            title: {
                text: this.spec.title,
                style: { color: text, fontSize: '0.875rem', fontWeight: '600' },
            },
            xAxis: {
                type: 'datetime',
                lineColor: gridLine,
                tickColor: gridLine,
                labels: { style: { color: subText } },
            },
            yAxis: {
                title: this.spec.yAxisLabel ? { text: this.spec.yAxisLabel, style: { color: subText } } : { text: null },
                gridLineColor: gridLine,
                labels: { style: { color: subText } },
            },
            legend: {
                enabled: (this.spec.series ?? []).length > 1,
                itemStyle: { color: subText, fontWeight: '400' },
                itemHoverStyle: { color: text },
            },
            tooltip: { shared: true, xDateFormat: '%Y-%m-%d %H:%M' },
            plotOptions: {
                series: { marker: { enabled: false, symbol: 'circle' } },
                area: { fillOpacity: 0.25 },
            },
            series: (this.spec.series ?? []).map((series) => ({
                name: series.name,
                data: (series.points ?? [])
                    .map((point) => [Date.parse(point.timestamp), point.value])
                    // A point the backend timestamped in a format Date.parse can't
                    // read would otherwise plot at the epoch and skew the whole axis.
                    .filter(([timestamp]) => !isNaN(timestamp as number)),
            })),
        };
    }

    /** Map the assistant's chart type onto the Highcharts series type. */
    private chartType(): string {
        if (this.spec.type === 'BAR') return 'column';
        if (this.spec.type === 'AREA') return 'area';
        return 'line';
    }

    /** Resolve a console theme variable, falling back to its light-mode value. */
    private cssVar(name: string, fallback: string): string {
        if (typeof getComputedStyle !== 'function') return fallback;
        const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        // Some of the console's variables carry `!important`, which is not part of the value.
        return value ? value.replace('!important', '').trim() : fallback;
    }

    /**
     * Watch the host element and re-lay-out whenever it changes size. The chat
     * panel expands and shrinks under the chart, and its transcript scrolls, so
     * the width at construction time is regularly not the final one.
     */
    private observeResize(): void {
        if (this.resizeObserver || typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width ?? 0;
            if (width > 0 && width !== this.lastWidth) {
                this.lastWidth = width;
                this.reflow();
            }
        });
        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    private scheduleReflows(): void {
        [0, 150, 350].forEach((delay) => {
            this.reflowTimers.push(setTimeout(() => this.reflow(), delay));
        });
    }

    private reflow(): void {
        // ref$ is an AsyncSubject: it replays the chart instance to this subscriber
        // (or fires once it's created) and completes, so there is nothing to unsubscribe.
        this.chart?.ref$?.subscribe((chart) => {
            try {
                chart?.reflow();
            } catch {
                // chart may be mid-recreate / already destroyed — safe to ignore.
            }
        });
    }
}
